import { useEffect, useRef, useState } from 'react';

import {
  EsignInvitePreviewQuery,
  useEsignInvitePreviewQuery,
} from 'core/graphql';

export type IContractInvitePreview = NonNullable<
  EsignInvitePreviewQuery['contractInvitePreview']
>;

/** ERP #133: шаблон, которым можно отправить приглашение. */
export type IContractInviteTemplateOption = IContractInvitePreview['templates'][number];

/** ERP #133: поле выбора шаблона в ошибках превью и отправки. */
export const INVITE_TEMPLATE_FIELD = 'templateId';

/** ТЗ 0094: разовая правка письма — сырой текст с `{…}`. */
export interface IInviteOverride {
  subject?: string;
  body?: string;
}

/** Пауза перед пере-запросом предпросмотра, пока человек печатает. */
export const INVITE_PREVIEW_DEBOUNCE_MS = 400;

/** Через `delay` мс после последнего изменения — значение, иначе прежнее. */
const useDebounced = <T>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

export interface IContractInvitePreviewState {
  /** Последний пришедший предпросмотр (на время пере-запроса — прежний). */
  preview: IContractInvitePreview | null;
  /** Первый ответ ещё не пришёл. */
  loading: boolean;
  /** Предпросмотр отстаёт от правки: пауза в наборе или пере-запрос в пути. */
  refreshing: boolean;
  /** Сеть или отказ — предпросмотра нет. */
  failed: boolean;
  /**
   * Пере-запрос (смена шаблона или правка) упал, а показан прежний ответ:
   * без этого флага окно молча держало старое письмо, а «Отправить»
   * оставалась выключенной без причины.
   */
  refreshFailed: boolean;
  /** Ответ `null`: договор не найден или ведёт его не вызывающий. */
  missing: boolean;
  /**
   * ERP #133: с каким `templateId` запрошен показанный предпросмотр
   * (`null` — без выбора, по правилам 0094; `undefined` — ответа ещё не
   * было). На время пере-запроса — прежний: показан ответ на прежний выбор.
   */
  previewFor: string | null | undefined;
  reload: () => void;
}

/**
 * ТЗ 0094 — письмо-приглашение так, как оно уйдёт при отправке договора
 * сейчас (`contractInvitePreview`): от кого, кому, своя или общая версия
 * шаблона, тема и текст с подставленными значениями (ссылка — образец).
 *
 * `override` — разовая правка (сырой текст с `{…}`): предпросмотр
 * пере-запрашивается с ней после паузы в наборе; ошибку правки сервер
 * возвращает в `errorCode`, текст при этом всё равно собирается.
 *
 * ERP #133: `templateId` — шаблон, выбранный в окне (`null` — по правилам
 * 0094); ответ несёт список выбора и id взятого шаблона.
 *
 * Без кэша: предпросмотр — снимок состояния ящика, шаблона и договора на
 * эту минуту, и при следующем открытии окна он должен быть свежим.
 */
export const useContractInvitePreview = (
  documentId: string,
  override: IInviteOverride | null,
  enabled = true,
  templateId: string | null = null,
): IContractInvitePreviewState => {
  // Строки, а не объект правки: новый объект на каждый рендер
  // перезапускал бы таймер без конца.
  const typedSubject = override?.subject ?? '';
  const typedBody = override?.body ?? '';
  const subject = useDebounced(typedSubject, INVITE_PREVIEW_DEBOUNCE_MS);
  const body = useDebounced(typedBody, INVITE_PREVIEW_DEBOUNCE_MS);
  // Правка ещё не дошла до запроса (пауза в наборе): показанный
  // предпросмотр — от прежнего текста, «Отправить» его не подтверждает.
  const behind = subject !== typedSubject || body !== typedBody;
  const variables = {
    documentId,
    ...(subject ? { emailSubject: subject } : {}),
    ...(body ? { emailBody: body } : {}),
    ...(templateId ? { templateId } : {}),
  };
  const { data, loading, error, refetch } = useEsignInvitePreviewQuery({
    variables,
    skip: !enabled,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  // Последний НАСТОЯЩИЙ ответ. Без кэша (`no-cache`) Apollo на время
  // запроса и при ошибке отдаёт в `data` пустышку `{}` без поля, и та же
  // пустышка попадает в `previousData`: упавший пере-запрос выглядел бы
  // как «договор не найден», а не как прежнее письмо с ошибкой.
  const fresh =
    !error && data && 'contractInvitePreview' in data ? data : undefined;
  const lastGood = useRef<EsignInvitePreviewQuery | undefined>(undefined);
  if (fresh) lastGood.current = fresh;
  const current = fresh ?? lastGood.current;
  const preview = current?.contractInvitePreview ?? null;
  // Ответ пришёл и запрос не в пути — он на нынешний выбор шаблона.
  const shownFor = useRef<string | null | undefined>(undefined);
  if (fresh && !loading) shownFor.current = templateId;
  return {
    preview,
    loading: loading && !current,
    refreshing: (loading && Boolean(current)) || (enabled && behind),
    failed: Boolean(error) && !current,
    refreshFailed: Boolean(error) && Boolean(current) && !loading,
    missing: Boolean(current) && preview === null && !loading,
    previewFor: shownFor.current,
    reload: () => {
      refetch().catch(() => undefined);
    },
  };
};
